import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApi } from '@/api';
import type { User } from '@omnes/shared/schemas';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

interface RegisterData {
  name: string;
  email: string;
  password: string;
  role?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });
  const navigate = useNavigate();
  const location = useLocation();

  const refreshUser = useCallback(async () => {
    try {
      const user = await authApi.getCurrentUser();
      setState({ user, isAuthenticated: true, isLoading: false });
    } catch {
      setState({ user: null, isAuthenticated: false, isLoading: false });
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        try {
          const user = await authApi.getCurrentUser();
          setState({ user, isAuthenticated: true, isLoading: false });
        } catch {
          localStorage.removeItem('auth_token');
          setState({ user: null, isAuthenticated: false, isLoading: false });
        }
      } else {
        setState({ user: null, isAuthenticated: false, isLoading: false });
      }
    };
    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    setState(prev => ({ ...prev, isLoading: true }));
    const { user, token } = await authApi.login(email, password);
    localStorage.setItem('auth_token', token);
    setState({ user, isAuthenticated: true, isLoading: false });
    
    const from = location.state?.from?.pathname || '/admin';
    navigate(from, { replace: true });
  };

  const register = async (data: RegisterData) => {
    setState(prev => ({ ...prev, isLoading: true }));
    const { user, token } = await authApi.register(data);
    localStorage.setItem('auth_token', token);
    setState({ user, isAuthenticated: true, isLoading: false });
    navigate('/admin');
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    setState({ user: null, isAuthenticated: false, isLoading: false });
    navigate('/login');
  };

  return (
    <AuthContext.Provider value={{ ...state, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}